import { useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextSelection, NodeSelection } from "@tiptap/pm/state";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Youtube as YtIcon, Undo2, Redo2, AlignLeft, AlignCenter, Minus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const extensions = (placeholder) => [
  StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true } }),
  Image.configure({ HTMLAttributes: { class: "rte-image" } }),
  Youtube.configure({ nocookie: true, width: 640, height: 360, HTMLAttributes: { class: "rte-youtube" } }),
  Placeholder.configure({ placeholder }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
];

function Tool({ onClick, active, title, children, testid }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title} className={`h-8 w-8 flex items-center justify-center transition-colors ${active ? "bg-zinc-900 text-white" : "hover:bg-zinc-200"}`} data-testid={testid}>
      {children}
    </button>
  );
}

const afterNode = ({ tr, state }) => {
  if (!(tr.selection instanceof NodeSelection)) return true;
  const pos = tr.selection.to;
  const next = tr.doc.resolve(pos).nodeAfter;
  if (!next || !next.isTextblock) tr.insert(pos, state.schema.nodes.paragraph.create());
  tr.setSelection(TextSelection.create(tr.doc, pos + 1));
  return true;
};

function Toolbar({ editor, onImage }) {
  const c = () => editor.chain().focus();
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link (URL)", prev);
    if (url === null) return;
    url ? c().extendMarkRange("link").setLink({ href: url }).command(({ tr }) => { tr.setSelection(TextSelection.create(tr.doc, tr.selection.to)); return true; }).run() : c().extendMarkRange("link").unsetLink().run();
  };
  const youtube = () => {
    const url = window.prompt("YouTube-link");
    if (!url) return;
    if (!editor.can().setYoutubeVideo({ src: url })) return toast.error("Dit is geen geldige YouTube-link");
    c().setYoutubeVideo({ src: url }).command(afterNode).run();
  };
  const sep = <span className="w-px h-5 bg-zinc-300 mx-1" />;
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-zinc-900 bg-zinc-50 p-1 sticky top-0 z-10" data-testid="rte-toolbar">
      <Tool onClick={() => c().toggleBold().run()} active={editor.isActive("bold")} title="Vet" testid="rte-bold"><Bold className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleItalic().run()} active={editor.isActive("italic")} title="Cursief" testid="rte-italic"><Italic className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleUnderline().run()} active={editor.isActive("underline")} title="Onderstreept"><Underline className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleStrike().run()} active={editor.isActive("strike")} title="Doorhalen"><Strikethrough className="h-4 w-4" /></Tool>
      {sep}
      <Tool onClick={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Kop" testid="rte-h2"><Heading2 className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subkop"><Heading3 className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Opsomming" testid="rte-bullets"><List className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Genummerd"><ListOrdered className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citaat"><Quote className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().setHorizontalRule().run()} title="Scheidingslijn"><Minus className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Links uitlijnen"><AlignLeft className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centreren"><AlignCenter className="h-4 w-4" /></Tool>
      {sep}
      <Tool onClick={link} active={editor.isActive("link")} title="Link" testid="rte-link"><Link2 className="h-4 w-4" /></Tool>
      <Tool onClick={onImage} title="Afbeelding uploaden" testid="rte-image"><ImagePlus className="h-4 w-4" /></Tool>
      <Tool onClick={youtube} title="YouTube-video" testid="rte-youtube"><YtIcon className="h-4 w-4" /></Tool>
      {sep}
      <Tool onClick={() => c().undo().run()} title="Ongedaan maken"><Undo2 className="h-4 w-4" /></Tool>
      <Tool onClick={() => c().redo().run()} title="Opnieuw"><Redo2 className="h-4 w-4" /></Tool>
    </div>
  );
}

export const RichTextEditor = ({ value, onChange, orgId, editable = true, placeholder = "Beschrijf het gedrag uitgebreid…" }) => {
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Afbeelding uploaden…");
    try {
      const { data } = await api.post(`/organizations/${orgId}/uploads`, fd);
      editorRef.current?.chain().focus().setImage({ src: `${BACKEND}${data.url}`, alt: file.name }).command(afterNode).run();
      toast.success("Afbeelding toegevoegd", { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Uploaden mislukt", { id: t });
    }
  };
  const editor = useEditor({
    extensions: extensions(placeholder),
    content: value || "",
    editable,
    onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML()),
    editorProps: {
      attributes: { class: "rte-content", "data-testid": "rte-content" },
      handlePaste: (_v, e) => {
        const f = [...(e.clipboardData?.files || [])].find((x) => x.type.startsWith("image/"));
        if (f && editable) { upload(f); return true; }
        return false;
      },
      handleDrop: (_v, e) => {
        const f = [...(e.dataTransfer?.files || [])].find((x) => x.type.startsWith("image/"));
        if (f && editable) { e.preventDefault(); upload(f); return true; }
        return false;
      },
    },
  });
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  if (!editor) return null;
  return (
    <div className={`rte ${editable ? "border border-zinc-900" : ""}`} data-testid={editable ? "rich-text-editor" : "rich-text-view"}>
      {editable && <Toolbar editor={editor} onImage={() => fileRef.current?.click()} />}
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} data-testid="rte-image-input" />
    </div>
  );
};
